<template>
    <div class="mt-6">
        <div class="mb-6 px-2 sm:px-4 flex flex-col items-center text-center" v-if="!isCollapsed">

            <div v-if="currentBadgeState === 'freeTrial' && client"
                 class="w-full max-w-xs text-center "
            >
                <div
                    @click="redirectToPlansPage"
                    :style="{ backgroundColor: '#DDDDDD', color: '#000000' }"
                    :class="badgeBaseClasses"
                    class="hover:shadow-xl transition-shadow duration-300 ease-in-out focus:outline-none hover:ring-2 hover:ring-primary-500 hover:ring-opacity-50 cursor-pointer mb-2"
                >
                    Teste Grátis
                </div>
                <div v-if="remainingTimeStr" :class="trialCountdownClasses" class="text-gray-300 dark:text-gray-300">
                    {{ remainingTimeStr }}
                </div>
            </div>

            <div
                 @click="redirectToPlansPage"
                v-else-if="currentBadgeState === 'activePlan' && (client?.active_subscription?.related_plan || client?.subscription)"
                :style="{ backgroundColor: '#FBBC05', color: '#000000' }"
                :class="badgeBaseClasses"
                class="w-full max-w-xs cursor-pointer hover:shadow-xl"
            >
                <StarIcon class="h-4 w-4 mr-1.5 inline-block"/>
                <span>{{ client?.subscription?.plan.name ?? client?.active_subscription?.related_plan.name  }}</span>
            </div>

            <div
                v-else-if="currentBadgeState === 'noPlan'"
                :style="{ backgroundColor: '#DDDDDD', color: '#000000' }"
                :class="badgeBaseClasses"
                class="w-full max-w-xs cursor-pointer hover:opacity-90"
                @click="redirectToPlansPage"
            >
                Escolha seu Plano
            </div>
        </div>
        <div id="tour-step-4-sidebar">
            <nav class="mt-12 space-y-4 flex flex-col items-center">
                <SidebarItem
                    id="tour-step-1-dashboard"
                    class="text-white"
                    :href="route('craftable-pro.dashboard')"
                    :icon="HomeIcon"
                    :icon-view="isCollapsed"
                >
                    Dashboard
                </SidebarItem>

                <SidebarItem
                    class="text-white"
                    :href="route('craftable-pro.client-matched-jobs.index')"
                    :icon="MagnifyingGlassIcon"
                    :icon-view="isCollapsed"
                    id="tour-step-5-radar"
                >
                    Radar de Vagas
                </SidebarItem>

                <SidebarItem
                    class="text-white"
                    :href="route('craftable-pro.documents.hub')"
                    :icon="DocumentTextIcon"
                    :icon-view="isCollapsed"
                >
                    Meus Documentos
                </SidebarItem>

                <SidebarItem
                    class="text-white"
                    :href="route('craftable-pro.relocation-pack.index')"
                    :icon="SparklesIcon"
                    :icon-view="isCollapsed"
                >
                    Pack de Entrevistas
                    <span class="ml-2 inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide">Novo</span>
                </SidebarItem>

                <SidebarItem
                    class="text-white"
                    :href="route('craftable-pro.interviews.hub')"
                    :icon="ChatBubbleLeftRightIcon"
                    :icon-view="isCollapsed"
                >
                    Minhas Entrevistas
                </SidebarItem>

                <SidebarItem
                    class="text-white"
                    :href="route('craftable-pro.networking-contacts.index')"
                    :icon="RectangleGroupIcon"
                    :icon-view="isCollapsed"
                >
                    Meu Relacionamento Estratégico
                </SidebarItem>

                <SidebarItem
                    class="text-white"
                    :href="route('craftable-pro.trainings.indexClient')"
                    :icon="AcademicCapIcon"
                    :icon-view="isCollapsed"

                >
                    Treinamentos
                </SidebarItem>

            </nav>
        </div>

        <Teleport to="body">
            <div
                v-if="shouldShowOverlay"
                class="fixed z-[9998] rounded-lg pointer-events-none transition-all duration-300"
                :style="spotlightStyle"
            ></div>

            <TourTip
                :title="currentStep.title"
                v-if="tipPosition.visible && shouldShowTipForCurrentStep('#tour-step-1-dashboard')"
                :placement="tipPosition.placement"
                v-bind="commonTipProps"
                @next="nextStep"
                @finish="finishTour"
            />
            <TourTip
                :title="currentStep.title"
                v-if="tipPosition.visible && shouldShowTipForCurrentStep('#tour-step-5-radar')"
                :placement="tipPosition.placement"
                v-bind="commonTipProps"
                @next="nextStep"
                @finish="finishTour"
            />
            <TourTip
                :title="currentStep.title"
                v-if="tipPosition.visible && shouldShowTipForCurrentStep('#tour-step-4-sidebar')"
                :placement="tipPosition.placement"
                v-bind="commonTipProps"
                @next="nextStep"
                @finish="finishTour"
            />
        </Teleport>
    </div>
</template>

<script setup lang="ts">
// --- Importações de Dependências ---
import {computed, onMounted, ref, watch, onUnmounted, nextTick} from "vue";
import {router, usePage} from '@inertiajs/vue3';
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from 'dayjs/plugin/duration';

// --- Importações de Ícones e Componentes (mantendo suas importações originais) ---
import {
    KeyIcon, LanguageIcon, UsersIcon, Cog8ToothIcon, MapIcon, BriefcaseIcon,
    LightBulbIcon, VideoCameraIcon, MagnifyingGlassPlusIcon, ListBulletIcon,
    PresentationChartLineIcon, HomeIcon, DocumentMagnifyingGlassIcon, XCircleIcon,
    ArchiveBoxXMarkIcon, ArchiveBoxArrowDownIcon, UserGroupIcon, FolderOpenIcon,
    BellIcon, EyeIcon, BellAlertIcon, EnvelopeIcon, ArrowsPointingOutIcon,
    MegaphoneIcon, TrophyIcon, CircleStackIcon, DocumentChartBarIcon,
    ClockIcon, BanknotesIcon, DocumentTextIcon, ChatBubbleLeftRightIcon,
    Square3Stack3DIcon, PhotoIcon, SparklesIcon,

    MagnifyingGlassIcon,
    RectangleGroupIcon,
    DocumentDuplicateIcon
} from "@heroicons/vue/24/outline";
import {
    AcademicCapIcon, ArrowTrendingUpIcon, BuildingOffice2Icon, ClipboardDocumentListIcon,
    DocumentCheckIcon, ExclamationCircleIcon,
    UserIcon,
    StarIcon, // Usado no badge
    IdentificationIcon // Ícone sólido 'IdentificationIcon'
} from "@heroicons/vue/20/solid";

import SidebarItem from "./SidebarItem.vue";
import SidebarGroup from "./SidebarGroup.vue";
import ThemeToggle from "@/craftable-pro/Components/Theme/ThemeToggle.vue";
import {useClient} from "@/hooks/useClient";
import {useOnboarding} from "@/Composables/useOnboarding";
import TourTip from "@/craftable-pro/Components/Tip/TourTip.vue";

// --- Configuração de Day.js ---
dayjs.extend(customParseFormat);
dayjs.extend(duration);

// --- Props do Componente (mantendo as suas originais) ---
interface Props {
    user?: any; // Não usado pela lógica do badge, mas mantido
    isCollapsed: boolean;
}

const props = defineProps<Props>();

// --- Lógica do Badge e Componente ---
const {client, currentBadgeState} = useClient();

const remainingTimeStr = ref<string>('');
let countdownInterval: number | undefined = undefined;

// MODIFICADO: A lógica agora lê 'trial_expires_at' diretamente.
const calculatedTrialEndDate = computed<dayjs.Dayjs | null>(() => {
    // Verifica se o cliente está em trial e se a data de expiração existe.
    if (client.value?.is_trial && client.value.trial_expires_at) {
        const expiresAtDate = dayjs(client.value.trial_expires_at);

        // Valida se a data foi parseada corretamente pelo dayjs.
        if (expiresAtDate.isValid()) {
            return expiresAtDate;
        } else {
            console.error("Data 'client.trial_expires_at' inválida com dayjs:", client.value.trial_expires_at);
            return null;
        }
    }
    // Se não estiver em trial ou a data for nula, retorna null.
    return null;
});

// A função updateCountdown permanece EXATAMENTE A MESMA,
// pois ela já foi projetada para funcionar com o resultado de 'calculatedTrialEndDate'.
function updateCountdown(): void {
    if (calculatedTrialEndDate.value) {
        const now = dayjs();
        const endDate = calculatedTrialEndDate.value;

        if (endDate.isBefore(now)) { // Removido isSame, pois a expiração é no exato momento.
            remainingTimeStr.value = 'Período de teste esgotado!';
            if (countdownInterval) clearInterval(countdownInterval);
            return;
        }

        const diffMilliseconds = endDate.diff(now);
        const trialDuration = dayjs.duration(diffMilliseconds);

        const days = trialDuration.days();
        const hours = trialDuration.hours();
        const minutes = trialDuration.minutes();
        const seconds = trialDuration.seconds();

        // Constrói a string de forma mais legível
        const parts: string[] = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${String(hours).padStart(2, '0')}h`);
        if (minutes > 0) parts.push(`${String(minutes).padStart(2, '0')}m`);
        parts.push(`${String(seconds).padStart(2, '0')}s`);

        remainingTimeStr.value = `Expira em: ${parts.join(' ')}`;
    } else {
        remainingTimeStr.value = '';
        if (countdownInterval) clearInterval(countdownInterval);
    }
}

// Lembre-se de iniciar e limpar o intervalo no ciclo de vida do componente
onMounted(() => {
    updateCountdown(); // Executa uma vez imediatamente
    countdownInterval = window.setInterval(updateCountdown, 1000);
});

onUnmounted(() => {
    if (countdownInterval) {
        clearInterval(countdownInterval);
    }
});


function startOrStopCountdown(): void {
    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = undefined;
    }
    if (currentBadgeState.value === 'freeTrial' && calculatedTrialEndDate.value && calculatedTrialEndDate.value.isAfter(dayjs())) {
        updateCountdown();
        countdownInterval = setInterval(updateCountdown, 1000) as unknown as number;
    } else if (currentBadgeState.value === 'freeTrial' && calculatedTrialEndDate.value && !calculatedTrialEndDate.value.isAfter(dayjs())) {
        remainingTimeStr.value = 'Período de teste esgotado!';
    } else {
        remainingTimeStr.value = '';
    }
}

watch([() => client.value, calculatedTrialEndDate], startOrStopCountdown, {immediate: true, deep: true});

onUnmounted(() => {
    if (countdownInterval) {
        clearInterval(countdownInterval);
    }
});

const badgeBaseClasses = "px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold flex items-center justify-center shadow-md";
const trialCountdownClasses = "text-xs text-center mt-1";

function redirectToPlansPage(): void {
    window.location.href = route('craftable-pro.plans.index');
}

// --- Restante do seu script existente (função getIcon) ---
const getIcon = (icon: string): any => { // Tipo de retorno 'any' conforme seu código original
    const iconsMap: Record<string, any> = {
        ['AcademicCapIcon']: AcademicCapIcon,
        ['ArrowTrendingUpIcon']: ArrowTrendingUpIcon,
        ['BuildingOffice2Icon']: BuildingOffice2Icon,
        ['ClipboardDocumentListIcon']: ClipboardDocumentListIcon,
        ['DocumentCheckIcon']: DocumentCheckIcon,
        ['DocumentDuplicateIcon']: DocumentDuplicateIcon,
        ['ExclamationCircleIcon']: ExclamationCircleIcon,
        ['HomeIcon']: HomeIcon,
        ['IdentificationIcon']: IdentificationIcon,
        ['LanguageIcon']: LanguageIcon,
        ['LightBulbIcon']: LightBulbIcon,
        ['ListBulletIcon']: ListBulletIcon,
        ['MapIcon']: MapIcon,
        ['PresentationChartLineIcon']: PresentationChartLineIcon,
        ['UserGroupIcon']: UserGroupIcon,
        ['UsersIcon']: UsersIcon,
        ['VideoCameraIcon']: VideoCameraIcon,
        ['Cog8ToothIcon']: Cog8ToothIcon,
        ['BellIcon']: BellIcon,
        ['EyeIcon']: EyeIcon,
        ['FolderOpenIcon']: FolderOpenIcon,
        ['MagnifyingGlassPlusIcon']: MagnifyingGlassPlusIcon,
        ['StarIcon']: StarIcon,
        ['XCircleIcon']: XCircleIcon,
        ['ArchiveBoxArrowDownIcon']: ArchiveBoxArrowDownIcon,
        ['ArchiveBoxXMarkIcon']: ArchiveBoxXMarkIcon,
    };
    return iconsMap[icon] || null;
};

const { isTourActive, currentStep, totalSteps, nextStep, finishTour, currentStepIndex } = useOnboarding();

const tipPosition = ref({ x: 0, y: 0, visible: false, placement: 'right' as 'right' | 'bottom' });
// ✨ Novo: Posição e dimensões do holofote
const spotlightRect = ref({ top: 0, left: 0, width: 0, height: 0 });

// Computada para controlar a visibilidade do overlay/holofote
const shouldShowOverlay = computed(() => tipPosition.value.visible);

// Computada para a cor do overlay (sua lógica original, perfeita para isso)
const overlayBackgroundColor = computed(() => {
    if (!shouldShowOverlay.value) return 'transparent';
    const target = currentStep.value?.target;
    if (target === '#tour-step-5-radar' || target === '#tour-step-5-logout-button') {
        return 'rgba(10, 132, 255, 0.5)';
    }
    return 'rgba(0, 0, 0, 0.5)'; // Mudado para um overlay escuro suave para a Dica 1
});

// ✨ Novo: Estilo dinâmico para o holofote
const spotlightStyle = computed(() => {
    const { top, left, width, height } = spotlightRect.value;
    const color = overlayBackgroundColor.value;

    return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`,
        // A mágica acontece aqui: uma sombra enorme que cria o overlay
        boxShadow: `0 0 0 9999px ${color}`
    };
});

const commonTipProps = computed(() => ({
    text: currentStep.value.content,
    step: currentStepIndex.value,
    totalSteps: totalSteps.value,
    posX: tipPosition.value.x,
    posY: tipPosition.value.y,
}));
const shouldShowTipForCurrentStep = (targetId: string) => tipPosition.value.visible && currentStep.value?.target === targetId;

// 🔄 Função de cálculo agora atualiza AMBAS as posições
const calculatePositions = () => {
    tipPosition.value.visible = false;
    if (!isTourActive.value || !currentStep.value || currentStep.value.component !== 'SidebarContentClient') {
        return;
    }
    const targetElement = document.querySelector(currentStep.value.target);
    if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const PADDING = 8; // Espaçamento do holofote ao redor do elemento

        // Calcula a posição do holofote
        spotlightRect.value = {
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
        };

        // Calcula a posição da dica e a torna visível
        tipPosition.value = {
            x: rect.right + 20,
            y: rect.top + rect.height / 2 - 60,
            visible: true,
            placement: 'right'
        };
    }
};

// O watch agora chama a nova função
watch([currentStep, () => props.isCollapsed], async () => {
    await nextTick();
    calculatePositions();
}, { deep: true, immediate: true });

</script>

<style scoped>
</style>
